import { observable, action } from 'mobx'
import { has } from 'lodash'
import { IPrismaClientStartRPC, IProjectGetRPC } from '@prisma/studio-types'

import ProjectStore from 'stores/project/ProjectStore'
import DMMFStore from 'stores/dmmf/DMMFStore'
import PersistenceStore from 'stores/persistence/PersistenceStore'
import { IProjectSchema } from 'stores/persistence/types'
import ThemeStore from 'stores/theme/ThemeStore'
import ErrorStore from 'stores/error/ErrorStore'
import LayoutStore from 'stores/layout/LayoutStore'
import TabStore from 'stores/tab/TabStore'
import SessionStore from 'stores/session/SessionStore'
import ScriptStore from 'stores/script/ScriptStore'
import ModelStore from 'stores/model/ModelStore'

import getProjectId from 'utils/getProjectId'
import getStoreDependencies from 'utils/getStoreDependencies'
import getAllCounts from 'utils/getAllCounts'
import createError from 'utils/createError'

class BootstrapStore {
  private disposeProjectReaction: any

  @observable ready: boolean = false

  @action
  init = async () => {
    console.time('Bootstrap Duration')

    this.update({ ready: false })
    if (this.disposeProjectReaction) {
      this.disposeProjectReaction()
    }

    // The aim of this method is to get all stores ready, so:
    // 1. Generate Prisma Client and get the DMMF
    // 2. Initialize the ProjectStore
    // 3. Load all other stores from IndexedDB
    // 4. Run `count` queries for all models (This is done here rather than ModelStore so we can batch all of them together)
    // 5. Fix frozen scripts
    // 6. Load openTabs etc. from IndexedDB

    // The above steps might seem arbitrary (and they kinda are), but the point of this method is to make sure that we're not left in an inconsistent state.
    // This is likely because models, fields & enums might change between consecutive executions of this method
    // So we need to make sure that everything that depends on models, fields and enums doesn't get to an inconsistent state

    try {
      // 1. Generate Prisma Client and get the DMMF
      const {
        error: prismaResponseError,
        data: prismaResponseData,
      } = await window.transport.request<IPrismaClientStartRPC>({
        channel: 'prisma',
        action: 'clientStart',
        payload: {
          data: null,
        },
      })

      if (prismaResponseError || !prismaResponseData) {
        throw new Error(
          `Error starting Prisma Client: ${JSON.stringify(
            prismaResponseError,
            null,
            2
          )}`
        )
      }

      const { dmmf, datasourceProvider } = prismaResponseData

      // 2. Figure out the project that is opened in this window, and initialize the ProjectStore
      const {
        error: projectResponseError,
        data: projectResponseData,
      } = await window.transport.request<IProjectGetRPC>({
        channel: 'project',
        action: 'get',
        payload: {
          data: null,
        },
      })

      if (projectResponseError) {
        throw new Error(`Error fetching project: ${projectResponseError}`)
      }
      const { name: projectName, schemaPath } = projectResponseData
      // TODO:: Get rid of this concept of a "projectId", it is unnecessary. A `schemaPath` is unique enough to be treated as an ID
      const projectId = getProjectId(schemaPath)

      // There's a ProjectStore.add here because we do not know if this project has been opened before (AKA exists in IndexedDB already)
      // If it has been, the IndexedDB version will overwrite this
      // Either way, we make sure that this project always exists (and is active)
      ProjectStore.add(
        {
          id: projectId,
          name: projectName,
          schemaPath,
          recentModelIds: [], // Will be populated correctly if this project is already in IndexedDB (during restore)
        },
        { skipPersist: true }
      ) // After IndexedDB is ready, we'll force save this, so it is okay to `skipPersist` here
      ProjectStore.switch(projectId)

      // 3. initialize PersistenceStore (IndexedDB)
      await PersistenceStore.init({ projectId })

      // Now we start to load all stores from IndexedDB

      // First up is the DMMFStore
      DMMFStore.hydrate(dmmf, datasourceProvider)

      // Then all others, in order
      for (let store of getStoreDependencies()) {
        await store.restore()
      }

      // At this point, we have all models, fields and enums from the current DMMF & and from (possibly) a different DMMF that was loaded before Reconnecting.
      // Now we remove models, fields and enums that no longer exist in the DMMF (this may be because they were removed from the schema / a completely different schema was loaded)
      DMMFStore.removeUnusedModels(dmmf)
      DMMFStore.removeUnusedFields(dmmf)
      DMMFStore.removeUnusedEnums(dmmf)

      // 4. At this point, the only models in ModelStore are the ones that exist *currently* in the DMMF
      // So it is safe to run count queries for all of them.
      // We will batch them so they go out together. This will save a lot of back and forth for large schemas with lots of models

      if (window.requestIdleCallback) {
        window.requestIdleCallback(getAllCounts) // If RIC is available, schedule count request until later
      } else {
        await getAllCounts() // Otherwise, wait for it. We're waiting so that the main thread doesn't freeze after bootstrap is done (while resolving this promise)
      }

      // 5. Fix frozen scripts
      // Some frozen scripts might have reached an invalid state at this point. Frozen scripts cannot be changed by the user, but their dependencies, AKA fields might have changed, so we must fix this

      const frozenScripts = Object.values(ScriptStore.values).filter(
        s => s.frozen && ModelStore.get(s.modelId) // Ignore scripts whose models are gone, their days are limited anyway
      )
      frozenScripts.forEach(s => s.update({ fieldIds: s.model.fieldIds }))

      // 6. Now we attempt to reopen tabs
      const projectInIDB = (await PersistenceStore.load('projects')).find(
        p => p.id === projectId
      ) as IProjectSchema

      if (projectInIDB) {
        TabStore.hydrate({
          activeTabId: projectInIDB.activeTabId,
        })
        ThemeStore.apply(projectInIDB.theme)
      }

      // Since the active project might have been added before IndexedDB was ready, it is possible that it was never written to it. So we force save just to be safe
      await ProjectStore.activeProject!.forceUpdate()

      // 7. Mark everything as ready
      this.update({ ready: true })

      console.timeEnd('Bootstrap Duration')
    } catch (e) {
      // Mark everything as ready
      this.update({ ready: true })

      ErrorStore.update({
        type: 'fatal',
        description: 'Unable to load project',
        dump: `${e.message}\n${e.stack}`,
      })
      LayoutStore.updateError({ visible: true })

      throw createError(
        'BootstrapStore.init',
        'Studio bootstrap failed',
        {
          message: e.message,
          stack: e.stack,
        },
        e
      )
    }

    // All of the following happens after the app has loaded

    if (window.requestIdleCallback) {
      window.requestIdleCallback(this.cleanupIndexedDB)
    } else {
      this.cleanupIndexedDB()
    }
  }

  @action
  update = (value: { ready?: boolean } = {}) => {
    has(value, 'ready') && (this.ready = value.ready!)
  }

  cleanupIndexedDB = () => {
    // As a matter of housekeeping, we will also remove any sessions that aren't open in any tabs
    const tabSessionIds = Object.values(TabStore.openTabs).map(t => t.sessionId)
    Object.values(SessionStore.values)
      .filter(s => !tabSessionIds.includes(s.id))
      .forEach(s => SessionStore.remove(s.id))

    // As a matter of housekeeping, we will also remove any scripts that aren't part of any sessions
    const sessionScriptIds = Object.values(SessionStore.values).map(
      s => s.scriptId
    )
    Object.values(ScriptStore.values)
      .filter(s => !sessionScriptIds.includes(s.id) && s.name === null)
      .forEach(s => ScriptStore.remove(s.id))
  }
}

export default new BootstrapStore()
